/**
 * UnaddressedRequestsBanner
 *
 * A persistent, app-wide banner shown at the top of every page whenever there
 * are service-desk requests that need the current user's action (new/unaddressed).
 * It gives engineers an at-a-glance heads-up so a raised request is acted on
 * quickly. The banner disappears on its own once everything is addressed (total
 * drops to 0), and can be dismissed for the current session.
 */
import { useMemo, useState } from 'react';
import { Box, Typography, Alert, Button, Stack, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import useRequestBadgeCounts from '../hooks/useRequestBadgeCounts';

// Nav item id → { label, path } for building the summary chips + jump target.
const QUEUES = [
  { key: 'ticketPool', label: 'Ticket Pool', path: '/ticket-pool' },
  { key: 'tickets', label: 'Tickets', path: '/tickets' },
  { key: 'serverRequests', label: 'Server', path: '/server-requests' },
  { key: 'vpnRequests', label: 'VPN', path: '/vpn-requests' },
  { key: 'softwareRequests', label: 'Software', path: '/software-requests' },
  { key: 'hardwareRequests', label: 'Hardware', path: '/hardware/requests' },
  { key: 'reminders', label: 'Reminders', path: '/service-renewals' },
  { key: 'contractAnalysis', label: 'Contracts', path: '/contract-analysis' },
];

export default function UnaddressedRequestsBanner() {
  const navigate = useNavigate();
  const counts = useRequestBadgeCounts();
  const [dismissed, setDismissed] = useState(false);

  const active = useMemo(
    () => QUEUES
      .map((q) => ({ ...q, count: counts[q.key] || 0 }))
      .filter((q) => q.count > 0),
    [counts]
  );

  const total = counts.total || 0;

  if (total === 0 || dismissed || active.length === 0) return null;

  // Jump target = the queue with the most pending items.
  const primary = active.reduce((max, q) => (q.count > max.count ? q : max), active[0]);

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 2.5, md: 3 },
        pt: 1.5,
        maxWidth: '1600px',
        width: '100%',
        mx: 'auto',
      }}
    >
      <Alert
        severity="info"
        icon={<NotificationsActiveIcon />}
        onClose={() => setDismissed(true)}
        sx={{
          borderRadius: 1.5,
          alignItems: 'center',
          '& .MuiAlert-message': { flex: 1, width: '100%' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600}>
              {total} request{total > 1 ? 's need' : ' needs'} your attention
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
              {active.map((q) => (
                <Chip
                  key={q.key}
                  size="small"
                  label={`${q.label}: ${q.count}`}
                  onClick={() => navigate(q.path)}
                  sx={{ cursor: 'pointer', fontWeight: 500 }}
                />
              ))}
            </Stack>
          </Box>

          <Button
            color="info"
            size="small"
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate(primary.path)}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Review {primary.label}
          </Button>
        </Box>
      </Alert>
    </Box>
  );
}
